import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useRouteError } from 'react-router-dom';

/**
 * What an operator sees when a page throws.
 *
 * Most of these are the same story: the admin was deployed while their tab stayed open, so an
 * old bundle meets new data (or asks for a chunk that no longer exists). Reloading fixes it,
 * which is exactly what nobody thinks to try when the screen is a stack trace — they write to
 * support instead. So: say what happened in one line, put the reload button under it, and keep
 * the trace for whoever wants it.
 */

const RELOAD_MARKER = 'gmx-error-reloaded-at';
/** One automatic reload per five minutes — a crash that survives it is not a stale bundle. */
const RELOAD_COOLDOWN_MS = 5 * 60 * 1000;

/** A missing chunk is a deployed-over bundle, full stop: reload without asking. */
const isStaleBundleError = (error: unknown): boolean => {
  const name = (error as any)?.name ?? '';
  const message = String((error as any)?.message ?? error ?? '');
  return (
    name === 'ChunkLoadError' ||
    /Loading (CSS )?chunk [\d]+ failed/i.test(message) ||
    /dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message)
  );
};

const reloadOnce = (): boolean => {
  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_MARKER) ?? 0);
    if (Date.now() - last < RELOAD_COOLDOWN_MS) return false;
    window.sessionStorage.setItem(RELOAD_MARKER, String(Date.now()));
  } catch {
    // Private mode without storage: reloading once per crash is still better than a dead page.
  }
  window.location.reload();
  return true;
};

export const AppErrorScreen: React.FC<{ error: unknown }> = ({ error }) => {
  const message = String((error as any)?.message ?? error ?? 'Unknown error');
  const stack = (error as any)?.stack as string | undefined;

  React.useEffect(() => {
    if (isStaleBundleError(error)) reloadOnce();
  }, [error]);

  return (
    <Box sx={{ maxWidth: 640, mx: 'auto', mt: 10, px: 3, textAlign: 'center' }}>
      <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
        This page needs a reload
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        The admin was most likely updated while this tab stayed open. Reloading picks up the new
        version — your saved work is not affected.
      </Typography>

      <Button variant="contained" size="large" startIcon={<RefreshIcon />} onClick={() => window.location.reload()}>
        Reload the page
      </Button>

      <Box component="details" sx={{ mt: 4, textAlign: 'left' }}>
        <Box component="summary" sx={{ cursor: 'pointer', fontSize: 12, color: 'text.secondary' }}>
          Technical details
        </Box>
        <Box
          component="pre"
          sx={{ mt: 1, p: 1.5, bgcolor: 'action.hover', borderRadius: 1, fontSize: 11, overflowX: 'auto', whiteSpace: 'pre-wrap' }}
        >
          {message}
          {stack ? `\n\n${stack}` : ''}
        </Box>
      </Box>
    </Box>
  );
};

/** Route-level handler: react-router hands the thrown error to this element. */
export const RouteErrorBoundary: React.FC = () => <AppErrorScreen error={useRouteError()} />;

/**
 * Class boundary for everything react-router does not catch — a render that throws inside a
 * lazily mounted branch, for instance.
 */
export class AppErrorBoundary extends React.Component<{ children: React.ReactNode }, { error: unknown }> {
  state: { error: unknown } = { error: null };

  static getDerivedStateFromError(error: unknown) {
    return { error };
  }

  render() {
    return this.state.error ? <AppErrorScreen error={this.state.error} /> : this.props.children;
  }
}

export default AppErrorBoundary;
